// src/components/BIAccessSection.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Database, Loader2, AlertCircle, CheckCircle2, XCircle,
  Key, RefreshCw, ShieldOff, Plus, ExternalLink,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import { apiCall } from '../utils/api';
import BICredentialsModal from './modals/BICredentialsModal';
import ConfirmDialog from './modals/ConfirmDialog';

/**
 * Shared BI access management UI.
 *
 * Used in two places:
 *   - Super-admin org detail page (apiPrefix="/super-admin/organizations/{org_id}")
 *   - Customer settings (apiPrefix="/me")
 *
 * The apiPrefix determines which endpoint set is called. Behavior is
 * identical otherwise.
 *
 * The component is self-contained: it manages its own loading state,
 * error state, modal state, and refreshes status after each action.
 *
 * Props:
 *   apiPrefix       - String. The base path for BI access endpoints.
 *                     For super-admin: "/super-admin/organizations/{org_id}/bi-access"
 *                     For customer:    "/me/bi-access"
 *   canMutate       - Boolean. If false, only the status is shown (no
 *                     provision/rotate/revoke buttons). Use this to gate
 *                     the customer view on the user's admin role.
 *   onActionDone    - Optional callback fired after any successful action.
 *                     Useful if the parent page wants to refresh other data.
 */
export default function BIAccessSection({ apiPrefix, canMutate = true, onActionDone }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState(null);

  // Credentials modal state (set when provision/rotate returns)
  const [credentialsModal, setCredentialsModal] = useState(null);

  // Confirm dialog state (for destructive revoke action)
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  const loadStatus = useCallback(async () => {
    setError('');
    try {
      const res = await apiCall(apiPrefix);
      if (res.ok) {
        setStatus(await res.json());
      } else if (res.status === 404) {
        // 404 from super-admin path means org not found; should not happen
        // from customer path
        setError('BI access endpoint not available.');
      } else if (res.status === 403) {
        setError('You do not have permission to view BI access status.');
      } else {
        setError('Could not load BI access status.');
      }
    } catch (err) {
      console.error('BI status load error', err);
      setError('Network error loading BI access status.');
    } finally {
      setLoading(false);
    }
  }, [apiPrefix]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleProvision = async () => {
    setBusyAction('provision');
    setError('');
    try {
      const res = await apiCall(apiPrefix, { method: 'POST' });
      if (res.ok) {
        const credentials = await res.json();
        setCredentialsModal({ credentials, action: 'provisioned' });
        onActionDone?.();
      } else if (res.status === 409) {
        setError('BI access is already provisioned for this organization.');
      } else if (res.status === 403) {
        setError('Admin role required to provision BI access.');
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || 'Provisioning failed.');
      }
    } catch (err) {
      console.error('Provision error', err);
      setError('Network error during provisioning.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleRotate = async () => {
    setBusyAction('rotate');
    setError('');
    try {
      const res = await apiCall(`${apiPrefix}/rotate`, { method: 'POST' });
      if (res.ok) {
        const credentials = await res.json();
        setCredentialsModal({ credentials, action: 'rotated' });
        onActionDone?.();
      } else if (res.status === 404) {
        setError('No active BI credential to rotate. Provision first.');
      } else if (res.status === 403) {
        setError('Admin role required to rotate password.');
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || 'Rotation failed.');
      }
    } catch (err) {
      console.error('Rotate error', err);
      setError('Network error during rotation.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleRevoke = async () => {
    setBusyAction('revoke');
    setError('');
    try {
      const res = await apiCall(apiPrefix, { method: 'DELETE' });
      if (res.ok) {
        await loadStatus();
        onActionDone?.();
      } else if (res.status === 403) {
        setError('Admin role required to revoke BI access.');
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || 'Revocation failed.');
      }
    } catch (err) {
      console.error('Revoke error', err);
      setError('Network error during revocation.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleCredentialsClose = () => {
    setCredentialsModal(null);
    loadStatus(); // refresh status to reflect new state
  };

  // ─── Render ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center text-[#a0a0a0]">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Loading BI access status...
        </CardContent>
      </Card>
    );
  }

  const isProvisioned = status?.provisioned && status.status === 'active';
  const isRevoked = status?.provisioned && status.status === 'revoked';

  return (
    <>
      <Card>
        <CardContent className="p-6">
          {/* Section header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                <Database className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">BI Access</h3>
                <p className="text-sm text-[#a0a0a0] mt-0.5">
                  Connect Power BI, Tableau, or any Postgres BI tool directly to your data.
                </p>
              </div>
            </div>

            {isProvisioned && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Active
              </Badge>
            )}
            {isRevoked && (
              <Badge className="bg-neutral-500/20 text-neutral-400 border-neutral-500/30">
                <XCircle className="h-3 w-3 mr-1" />
                Revoked
              </Badge>
            )}
          </div>

          {/* Error display */}
          {error && (
            <div className="flex items-start gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* State: not provisioned */}
          {!status?.provisioned && (
            <div className="p-4 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
              <p className="text-sm text-[#a0a0a0] mb-3">
                BI access is not yet set up. Provisioning creates a read-only
                Postgres role with access to your data, ready to plug into any
                BI tool.
              </p>
              {canMutate ? (
                <Button
                  onClick={handleProvision}
                  disabled={busyAction !== null}
                >
                  {busyAction === 'provision' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Provisioning...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Provision BI Access
                    </>
                  )}
                </Button>
              ) : (
                <p className="text-xs text-[#666]">
                  An admin in your organization can provision BI access.
                </p>
              )}
            </div>
          )}

          {/* State: revoked */}
          {isRevoked && (
            <div className="p-4 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
              <p className="text-sm text-[#a0a0a0] mb-3">
                BI access was previously provisioned but is currently revoked.
                You can re-provision to generate a fresh credential.
              </p>
              {canMutate && (
                <Button
                  onClick={handleProvision}
                  disabled={busyAction !== null}
                >
                  {busyAction === 'provision' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Re-provisioning...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Re-provision BI Access
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* State: active */}
          {isProvisioned && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
                <div>
                  <div className="text-xs text-[#a0a0a0] uppercase tracking-wide mb-1">
                    Username
                  </div>
                  <div className="text-sm text-white font-mono">{status.role_name}</div>
                </div>
                <div>
                  <div className="text-xs text-[#a0a0a0] uppercase tracking-wide mb-1">
                    Schema
                  </div>
                  <div className="text-sm text-white font-mono">{status.schema_name}</div>
                </div>
                <div>
                  <div className="text-xs text-[#a0a0a0] uppercase tracking-wide mb-1">
                    Created
                  </div>
                  <div className="text-sm text-white">
                    {status.created_at ? new Date(status.created_at).toLocaleString() : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[#a0a0a0] uppercase tracking-wide mb-1">
                    Last Rotated
                  </div>
                  <div className="text-sm text-white">
                    {status.last_rotated_at
                      ? new Date(status.last_rotated_at).toLocaleString()
                      : 'Never'}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                <Key className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-200">
                  Your password is not stored. If lost, use{' '}
                  <strong className="font-semibold">Rotate Password</strong>{' '}
                  to generate a new one. The old password will stop working immediately.
                </p>
              </div>

              {canMutate && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleRotate}
                    disabled={busyAction !== null}
                    variant="outline"
                  >
                    {busyAction === 'rotate' ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Rotating...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Rotate Password
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowRevokeConfirm(true)}
                    disabled={busyAction !== null}
                    variant="ghost"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <ShieldOff className="h-4 w-4 mr-2" />
                    Revoke Access
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* One-time credentials display modal */}
      {credentialsModal && (
        <BICredentialsModal
          credentials={credentialsModal.credentials}
          action={credentialsModal.action}
          onClose={handleCredentialsClose}
        />
      )}

      {/* Revoke confirmation */}
      <ConfirmDialog
        isOpen={showRevokeConfirm}
        onClose={() => setShowRevokeConfirm(false)}
        onConfirm={handleRevoke}
        title="Revoke BI Access?"
        message="The Postgres role will be dropped and the schema deleted. Any BI tool currently connected will lose access immediately. This cannot be undone, but you can re-provision later."
        confirmText="Revoke Access"
        variant="danger"
      />
    </>
  );
}